/**
 * Board Catalog — super-admin management of device_boards.
 *
 * Each board row declares the capabilities and feature flags the v2 customer
 * API publishes for every device assigned to it. The registry endpoint
 * supplies the valid vocabulary so the form renders toggles, not free text.
 *
 * Since migration 045 a board belongs to a hardware provider (manufacturer)
 * and carries an append-only price list. Pricing is set here or on the
 * provider's own page (which is where a manufacturer admin lands); the
 * shared BoardPriceModal does the work in both places.
 */
import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { IconPlus, IconX } from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin } from '../services/users';
import {
  getDeviceBoards,
  getBoardRegistry,
  createDeviceBoard,
  updateDeviceBoard,
  deleteDeviceBoard,
  boardLabel,
  formatMoney,
} from '../services/deviceBoards';
import type { DeviceBoard, DeviceBoardInput } from '../services/deviceBoards';
import { getManufacturers } from '../services/manufacturers';
import { BoardPriceModal, formatWhen } from '../components/BoardPriceModal';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  StatusChip,
  LoadingText,
  EmptyState,
  MutedText,
  Caption,
  Row,
  Stack,
  ModalOverlay,
  ModalCard,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  HelperText,
  ErrorAlert,
  FormSectionTitle,
  Checkbox,
  H2Title,
} from '../components/styled';

const BackLink = styled(Link)`
  color: ${({ theme }) => theme.font.color.secondary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};

  &:hover {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 ${({ theme }) => theme.spacing(3)};
`;

const ToggleHint = styled.div`
  margin-left: 24px;
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

/* One bordered group per capability: the capability toggle on top, its
   feature flags and supported-value pickers nested beneath. */
const CapabilityGroup = styled.div<{ enabled: boolean }>`
  border: 1px solid ${({ theme }) => theme.border.color.light};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme, enabled }) => enabled ? theme.background.primary : theme.background.secondary};
`;

const NestedControls = styled.div`
  margin: ${({ theme }) => theme.spacing(3)} 0 0 24px;
  padding-left: ${({ theme }) => theme.spacing(3)};
  border-left: 2px solid ${({ theme }) => theme.border.color.light};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const NestedLabel = styled.div`
  font-size: ${({ theme }) => theme.font.size.sm};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  color: ${({ theme }) => theme.font.color.primary};
`;

const ValueRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2, 4)};
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const PriceLine = styled.div`
  white-space: nowrap;
`;

const ProviderLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

interface BoardForm {
  manufacturer_id: string;
  board: string;
  display_name: string;
  capabilities: string[];
  features: Record<string, boolean>;
  supported: Record<string, string[]>;
  default_num_doors: string;
  default_power_type: 'battery' | 'plugin';
  is_default: boolean;
  notes: string;
}

const emptyForm = (featureSlugs: string[], supportedKeys: string[]): BoardForm => ({
  manufacturer_id: '',
  board: '',
  display_name: '',
  capabilities: [],
  features: Object.fromEntries(featureSlugs.map((s) => [s, false])),
  supported: Object.fromEntries(supportedKeys.map((k) => [k, []])),
  default_num_doors: '1',
  default_power_type: 'battery',
  is_default: false,
  notes: '',
});

const formFromBoard = (b: DeviceBoard, featureSlugs: string[], supportedKeys: string[]): BoardForm => ({
  manufacturer_id: b.manufacturer_id != null ? String(b.manufacturer_id) : '',
  board: b.board,
  display_name: b.display_name || '',
  capabilities: [...b.capabilities],
  // Registry entries a board hasn't set yet default to off / empty.
  features: Object.fromEntries(featureSlugs.map((s) => [s, b.features?.[s] === true])),
  supported: Object.fromEntries(supportedKeys.map((k) => [k, [...(b.supported?.[k] ?? [])]])),
  default_num_doors: String(b.default_num_doors ?? 1),
  default_power_type: b.default_power_type || 'battery',
  is_default: b.is_default,
  notes: b.notes || '',
});

function toInput(form: BoardForm): DeviceBoardInput {
  return {
    manufacturer_id: Number(form.manufacturer_id),
    board: form.board.trim(),
    display_name: form.display_name.trim() || null,
    capabilities: form.capabilities,
    features: form.features,
    supported: form.supported,
    default_num_doors: form.default_num_doors === '' ? 1 : Number(form.default_num_doors),
    default_power_type: form.default_power_type,
    is_default: form.is_default,
    notes: form.notes.trim() || null,
  };
}

function BoardCatalog() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState<'create' | 'edit' | null>(null);
  const [selected, setSelected] = useState<DeviceBoard | null>(null);
  const [pricingBoard, setPricingBoard] = useState<DeviceBoard | null>(null);
  const [form, setForm] = useState<BoardForm>(emptyForm([], []));
  const [error, setError] = useState<string | null>(null);

  if (!isSuperAdmin(user)) {
    return <Navigate to="/admin" replace />;
  }

  // Narrow to one provider when linked from a Hardware Provider page.
  const filterManufacturerId = searchParams.get('manufacturerId')
    ? Number(searchParams.get('manufacturerId'))
    : undefined;

  const { data: boards = [], isLoading } = useQuery({
    queryKey: ['device-boards', filterManufacturerId ?? 'all'],
    queryFn: () => getDeviceBoards({ manufacturerId: filterManufacturerId }),
  });
  const { data: registry } = useQuery({
    queryKey: ['device-board-registry'],
    queryFn: getBoardRegistry,
  });
  const { data: manufacturers = [] } = useQuery({
    queryKey: ['manufacturers'],
    queryFn: () => getManufacturers(),
  });

  const featureSlugs = registry?.features.map((f) => f.slug) ?? [];
  const supportedKeys = registry?.supported.map((s) => s.key) ?? [];
  const filterManufacturer = manufacturers.find((m) => m.id === filterManufacturerId);

  const closeModal = () => {
    setModal(null);
    setSelected(null);
    setError(null);
  };

  const onMutationError = (err: any, fallback: string) => {
    const body = err.response?.data;
    setError(body?.details?.join?.('; ') || body?.error || fallback);
  };

  const invalidateBoards = () => {
    queryClient.invalidateQueries({ queryKey: ['device-boards'] });
    queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
    queryClient.invalidateQueries({ queryKey: ['manufacturer'] });
  };

  const createMutation = useMutation({
    mutationFn: createDeviceBoard,
    onSuccess: () => { invalidateBoards(); closeModal(); },
    onError: (err) => onMutationError(err, 'Failed to create board'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: DeviceBoardInput }) => updateDeviceBoard(id, data),
    onSuccess: () => {
      invalidateBoards();
      queryClient.invalidateQueries({ queryKey: ['device'] });
      closeModal();
    },
    onError: (err) => onMutationError(err, 'Failed to update board'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteDeviceBoard,
    onSuccess: invalidateBoards,
    onError: (err) => onMutationError(err, 'Failed to delete board'),
  });

  const openCreate = () => {
    setForm({ ...emptyForm(featureSlugs, supportedKeys), manufacturer_id: filterManufacturerId ? String(filterManufacturerId) : '' });
    setModal('create');
    setError(null);
  };

  const openEdit = (b: DeviceBoard) => {
    setSelected(b);
    setForm(formFromBoard(b, featureSlugs, supportedKeys));
    setModal('edit');
    setError(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const data = toInput(form);
    if (modal === 'edit' && selected) {
      updateMutation.mutate({ id: selected.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const toggleCapability = (slug: string) => {
    setForm((f) => ({
      ...f,
      capabilities: f.capabilities.includes(slug)
        ? f.capabilities.filter((c) => c !== slug)
        : [...f.capabilities, slug],
    }));
  };

  const toggleFeature = (slug: string) => {
    setForm((f) => ({ ...f, features: { ...f.features, [slug]: !f.features[slug] } }));
  };

  const toggleSupportedValue = (key: string, value: string) => {
    setForm((f) => {
      const current = f.supported[key] ?? [];
      return {
        ...f,
        supported: {
          ...f.supported,
          [key]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
        },
      };
    });
  };

  const confirmDelete = (b: DeviceBoard) => {
    if (b.device_count > 0) {
      setError(`${b.device_count} device(s) are assigned to ${boardLabel(b)}; reassign them first.`);
      return;
    }
    if (window.confirm(`Delete ${boardLabel(b)} from the catalog?`)) {
      setError(null);
      deleteMutation.mutate(b.id);
    }
  };

  const pending = createMutation.isPending || updateMutation.isPending;

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <BackLink to="/admin">← Admin</BackLink>
          <PageTitle>Board Catalog</PageTitle>
        </div>
        <Button variant="primary" onClick={openCreate}>
          <IconPlus size={16} stroke={2} />
          Add Board
        </Button>
      </PageHeader>

      <MutedText>
        A board is a hardware generation. Its capabilities decide which blocks and
        command routes the customer API exposes for a device; its feature flags gate
        individual fields inside those blocks. Devices inherit all of it from the
        board they are assigned to. Each board belongs to a hardware provider, who
        sets its price list.
        {filterManufacturer && (
          <> Showing <strong>{filterManufacturer.name}</strong> only — <Link to="/admin/boards">show all</Link>.</>
        )}
      </MutedText>

      {error && modal === null && <ErrorAlert>{error}</ErrorAlert>}

      {isLoading ? (
        <LoadingText>Loading boards...</LoadingText>
      ) : boards.length === 0 ? (
        <EmptyState>
          <p>No boards in the catalog yet.</p>
        </EmptyState>
      ) : (
        <TableContainer>
          <StyledTable>
            <thead>
              <tr>
                <Th>Board</Th>
                <Th>Capabilities</Th>
                <Th>Features</Th>
                <Th>Defaults</Th>
                <Th>Pricing</Th>
                <Th>Devices</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {boards.map((b) => (
                <tr key={b.id}>
                  <Td>
                    <Stack gap={1}>
                      <Row gap={2} align="center">
                        <strong>{boardLabel(b)}</strong>
                        {b.is_default && <StatusChip variant="positive">default</StatusChip>}
                      </Row>
                      <Caption>
                        {b.manufacturer_id != null
                          ? <ProviderLink to={`/hardware-providers/${b.manufacturer_id}`}>{b.manufacturer}</ProviderLink>
                          : b.manufacturer}
                        {' · '}{b.board}
                      </Caption>
                      {b.notes && <Caption>{b.notes}</Caption>}
                    </Stack>
                  </Td>
                  <Td>
                    <ChipRow>
                      {b.capabilities.map((c) => (
                        <StatusChip key={c} variant="neutral">{c}</StatusChip>
                      ))}
                      {b.capabilities.length === 0 && <MutedText>—</MutedText>}
                    </ChipRow>
                  </Td>
                  <Td>
                    <ChipRow>
                      {featureSlugs.map((slug) => (
                        <StatusChip key={slug} variant={b.features?.[slug] ? 'positive' : 'neutral'}>
                          {slug}: {b.features?.[slug] ? 'on' : 'off'}
                        </StatusChip>
                      ))}
                      {supportedKeys.map((key) => (
                        <StatusChip key={key} variant={(b.supported?.[key]?.length ?? 0) > 0 ? 'positive' : 'neutral'}>
                          {key}: {b.supported?.[key]?.length ? b.supported[key].join(', ') : 'none'}
                        </StatusChip>
                      ))}
                    </ChipRow>
                  </Td>
                  <Td>
                    <Caption>
                      {b.default_num_doors} door{b.default_num_doors === 1 ? '' : 's'} · {b.default_power_type}
                    </Caption>
                  </Td>
                  <Td>
                    {b.current_price ? (
                      <Stack gap={1}>
                        <PriceLine>{formatMoney(b.current_price.hardware_price_cents, b.current_price.currency)} <Caption>hardware</Caption></PriceLine>
                        <PriceLine>{formatMoney(b.current_price.monthly_fee_cents, b.current_price.currency)} <Caption>/ device / month</Caption></PriceLine>
                        <Caption>since {formatWhen(b.current_price.effective_from)}</Caption>
                      </Stack>
                    ) : (
                      <StatusChip variant="warning">not priced</StatusChip>
                    )}
                  </Td>
                  <Td>{b.device_count}</Td>
                  <Td>
                    <Row gap={2}>
                      <Button size="sm" variant={b.current_price ? 'secondary' : 'primary'} onClick={() => setPricingBoard(b)}>
                        {b.current_price ? 'Change price' : 'Set price'}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => openEdit(b)}>Edit</Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={b.device_count > 0 || b.is_default || deleteMutation.isPending}
                        title={b.device_count > 0 ? 'Reassign its devices first' : b.is_default ? 'Pick another default first' : undefined}
                        onClick={() => confirmDelete(b)}
                      >
                        Delete
                      </Button>
                    </Row>
                  </Td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableContainer>
      )}

      {pricingBoard && (
        <BoardPriceModal board={pricingBoard} onClose={() => setPricingBoard(null)} />
      )}

      {modal && (
        <ModalOverlay onClick={closeModal}>
          <ModalCard size="lg" onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <H2Title>{modal === 'edit' ? `Edit ${selected ? boardLabel(selected) : 'board'}` : 'Add Board'}</H2Title>
              <ModalCloseButton onClick={closeModal} aria-label="Close">
                <IconX size={18} stroke={2} />
              </ModalCloseButton>
            </ModalHeader>
            <form onSubmit={submit}>
              <ModalBody>
                {error && <ErrorAlert>{error}</ErrorAlert>}

                <FormSectionTitle>Identity</FormSectionTitle>
                <FieldGrid>
                  <FormGroup>
                    <Label>Hardware provider</Label>
                    <Select
                      value={form.manufacturer_id}
                      onChange={(e) => setForm({ ...form, manufacturer_id: e.target.value })}
                      required
                    >
                      <option value="">Select a hardware provider</option>
                      {manufacturers.map((m) => (
                        <option key={m.id} value={m.id}>{m.name}{m.active ? '' : ' (inactive)'}</option>
                      ))}
                    </Select>
                    <HelperText>
                      Published as <code>device.manufacturer</code>. Add providers under{' '}
                      <Link to="/hardware-providers">Hardware Providers</Link>.
                    </HelperText>
                  </FormGroup>
                  <FormGroup>
                    <Label>Board</Label>
                    <Input
                      value={form.board}
                      onChange={(e) => setForm({ ...form, board: e.target.value })}
                      placeholder="SB6"
                      required
                    />
                    <HelperText>Published verbatim as <code>device.board</code> on the customer API.</HelperText>
                  </FormGroup>
                </FieldGrid>

                <FormGroup>
                  <Label>Display name</Label>
                  <Input
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    placeholder="Simkura SB6"
                  />
                </FormGroup>

                <FormSectionTitle>Defaults for new devices</FormSectionTitle>
                <FieldGrid>
                  <FormGroup>
                    <Label>Doors</Label>
                    <Input
                      type="number"
                      min={1}
                      max={32}
                      value={form.default_num_doors}
                      onChange={(e) => setForm({ ...form, default_num_doors: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Label>Power</Label>
                    <Select
                      value={form.default_power_type}
                      onChange={(e) => setForm({ ...form, default_power_type: e.target.value as BoardForm['default_power_type'] })}
                    >
                      {(registry?.powerTypes ?? ['battery', 'plugin']).map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </Select>
                  </FormGroup>
                </FieldGrid>

                <FormGroup>
                  <Checkbox
                    checked={form.is_default}
                    onChange={() => setForm({ ...form, is_default: !form.is_default })}
                    label="Default board for newly discovered devices"
                  />
                </FormGroup>

                <FormSectionTitle>What devices on this board support</FormSectionTitle>
                <MutedText>
                  Each group is one area of the customer API. Checking the group
                  exposes its endpoints and resource block; the items inside
                  refine what the hardware can do within it.
                </MutedText>

                {(registry?.capabilities ?? []).map((c) => {
                  const enabled = form.capabilities.includes(c.slug);
                  const groupFeatures = (registry?.features ?? []).filter((f) => f.capability === c.slug);
                  const groupSupported = (registry?.supported ?? []).filter((s) => s.capability === c.slug);
                  return (
                    <CapabilityGroup key={c.slug} enabled={enabled}>
                      <Checkbox
                        checked={enabled}
                        onChange={() => toggleCapability(c.slug)}
                        label={c.slug}
                      />
                      <ToggleHint>{c.description}</ToggleHint>

                      {enabled && (groupFeatures.length > 0 || groupSupported.length > 0) && (
                        <NestedControls>
                          {groupFeatures.map((f) => (
                            <div key={f.slug}>
                              <Checkbox
                                checked={form.features[f.slug] === true}
                                onChange={() => toggleFeature(f.slug)}
                                label={f.slug}
                              />
                              <ToggleHint>{f.description} <em>({f.gates})</em></ToggleHint>
                            </div>
                          ))}
                          {groupSupported.map((s) => (
                            <div key={s.key}>
                              <NestedLabel>{s.key}</NestedLabel>
                              <ToggleHint style={{ marginLeft: 0 }}>{s.description} <em>({s.operation})</em></ToggleHint>
                              <ValueRow>
                                {s.values.map((v) => (
                                  <Checkbox
                                    key={v}
                                    checked={(form.supported[s.key] ?? []).includes(v)}
                                    onChange={() => toggleSupportedValue(s.key, v)}
                                    label={v}
                                  />
                                ))}
                              </ValueRow>
                            </div>
                          ))}
                        </NestedControls>
                      )}
                    </CapabilityGroup>
                  );
                })}

                <FormGroup style={{ marginTop: 16 }}>
                  <Label>Notes</Label>
                  <TextArea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={3}
                    placeholder="What this board is, what it can't do yet"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" type="button" onClick={closeModal}>Cancel</Button>
                <Button variant="primary" type="submit" disabled={pending}>
                  {pending ? 'Saving...' : modal === 'edit' ? 'Save Changes' : 'Add Board'}
                </Button>
              </ModalFooter>
            </form>
          </ModalCard>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

export default BoardCatalog;
